import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import {
	ChevronDown,
	CreditCard,
	LayoutDashboard,
	LogOut,
	Menu,
	MessageSquare,
	Package,
	ShoppingCart,
	User,
	X,
} from "lucide-react";
import { ReactNode, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface DashboardLayoutProps {
	children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
	const { profile, signOut, isSupplier, isRetailer } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

	const handleSignOut = async () => {
		await signOut();
		navigate("/");
	};

	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	};

	const toggleUserMenu = () => {
		setIsUserMenuOpen(!isUserMenuOpen);
	};

	const isActive = (path: string) => {
		if (path === "/dashboard" && location.pathname !== "/dashboard")
			return false;
		return (
			location.pathname === path ||
			location.pathname.startsWith(`${path}/`)
		);
	};

	const navigationItems = [
		{
			name: "Dashboard",
			path: "/dashboard",
			icon: <LayoutDashboard className="h-5 w-5" />,
			showFor: "supplier",
		},
		{
			name: "Profile",
			path: "/dashboard/profile",
			icon: <User className="h-5 w-5" />,
			showFor: "both",
		},
		{
			name: "Products",
			path: "/dashboard/products",
			icon: <Package className="h-5 w-5" />,
			showFor: "supplier",
		},
		{
			name: "Messages",
			path: "/dashboard/messages",
			icon: <MessageSquare className="h-5 w-5" />,
			showFor: "both",
		},
		{
			name: "Orders",
			path: "/dashboard/orders",
			icon: <ShoppingCart className="h-5 w-5" />,
			showFor: "both",
		},
		{
			name: "Payments",
			path: "/dashboard/payments",
			icon: <CreditCard className="h-5 w-5" />,
			showFor: "supplier",
		},
	];

	const filteredNavItems = navigationItems.filter((item) => {
		if (item.showFor === "both") return true;
		if (item.showFor === "supplier" && isSupplier) return true;
		if (item.showFor === "retailer" && isRetailer) return true;
		return false;
	});

	return (
		<div className="min-h-screen bg-gray-50 flex flex-col">
			{/* Top Bar */}
			<header className="bg-white border-b border-gray-200 sticky top-0 z-10">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex justify-between h-16">
						<div className="flex">
							{/* Logo */}
							<div className="flex-shrink-0 flex items-center">
								<Link
									to={isSupplier ? "/dashboard" : "/"}
									className="font-display text-xl font-bold text-accent">
									Starch
								</Link>
							</div>
						</div>

						{/* Mobile menu button */}
						<div className="flex items-center sm:hidden">
							<button
								type="button"
								className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-accent hover:bg-accent"
								onClick={toggleMobileMenu}>
								{isMobileMenuOpen ? (
									<X className="block h-6 w-6" />
								) : (
									<Menu className="block h-6 w-6" />
								)}
							</button>
						</div>

						{/* Desktop menu */}
						<div className="hidden sm:flex sm:items-center sm:ml-6">
							<div className="relative ml-3">
								<div>
									<button
										type="button"
										className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none"
										onClick={toggleUserMenu}>
										<div className="flex items-center gap-2 border px-3 py-2 rounded-full hover:bg-gray-50 hover:border-gray-200">
											<div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-medium">
												{profile?.name?.[0] || "U"}
											</div>
											<span className="text-sm font-medium text-gray-700 hidden md:block">
												{profile?.name || "User"}
											</span>
											<ChevronDown className="h-4 w-4 text-gray-500" />
										</div>
									</button>
								</div>

								{isUserMenuOpen && (
									<div className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
										<div className="px-4 py-2 border-b">
											<p className="text-sm font-medium text-gray-700">
												{profile?.name}
											</p>
											<p className="text-xs text-gray-500 capitalize">
												{profile?.accountType}
											</p>
										</div>
										<button
											className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
											onClick={handleSignOut}>
											Sign out
										</button>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Mobile menu */}
				{isMobileMenuOpen && (
					<div className="sm:hidden border-t border-gray-200">
						<div className="pt-2 pb-3 space-y-1">
							{filteredNavItems.map((item) => (
								<Link
									key={item.path}
									to={item.path}
									className={`flex items-center px-3 py-2 text-base font-medium ${
										isActive(item.path)
											? "bg-accent text-accent"
											: "text-gray-600 hover:bg-accent/50 hover:text-accent"
									}`}
									onClick={() => setIsMobileMenuOpen(false)}>
									<span className="mr-3">{item.icon}</span>
									{item.name}
								</Link>
							))}
							<button
								className="flex w-full items-center px-3 py-2 text-base font-medium text-gray-600 hover:bg-accent/50 hover:text-accent"
								onClick={handleSignOut}>
								<LogOut className="mr-3 h-5 w-5" />
								Sign out
							</button>
						</div>
					</div>
				)}
			</header>

			<div className="flex-grow flex max-w-7xl w-full mx-auto">
				{/* Sidebar (desktop) */}
				<div className="hidden sm:flex sm:flex-col w-64 border-r border-gray-200 bg-white">
					<div className="flex-grow flex flex-col pt-5 pb-4 overflow-y-auto">
						<nav className="mt-5 flex-1 px-2 space-y-1">
							{filteredNavItems.map((item) => (
								<Link
									key={item.path}
									to={item.path}
									className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md ${
										isActive(item.path)
											? "bg-secondary text-secondary-dark"
											: "text-gray-600 hover:bg-secondary/50 hover:text-secondary-dark"
									}`}>
									<span className="mr-3">{item.icon}</span>
									{item.name}
								</Link>
							))}
						</nav>
					</div>
					<div className="flex-shrink-0 flex border-t border-gray-200 p-4">
						<Button
							variant="accentOutline"
							className="w-full justify-start text-accent"
							onClick={handleSignOut}>
							<LogOut className="mr-3 h-5 w-5" />
							Sign out
						</Button>
					</div>
				</div>

				{/* Main content */}
				<main className="flex-1 p-4 md:p-6 overflow-y-auto">
					{children}
				</main>
			</div>
		</div>
	);
}
